import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import {
  AiAgent,
  AiAgentSessionGoal,
  AiAgentToolCall,
  User,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { AssigneeLabel } from 'src/components/general/display/assignee-label';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { InteractionDetailsBadge } from 'src/components/pages/conversations/components/contact-interactions/items/phone-call-analysis/components/interaction-details-badge';
import { BaseBadge } from '../phone-call-analysis/components/base-badge';
import { InteractionTextContent } from '../shared';

export const SummaryBadge = ({
  isInProgress = false,
  onClick,
}: {
  isInProgress?: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation(['conversations']);

  return (
    <InteractionDetailsBadge
      icon="summary"
      title={t('conversations:contactInteractionBubble.phoneCall.aiCallAnalysis.summary')}
      type="neutral"
      inProgress={isInProgress}
      data-lgg-id="summary-badge"
      onClick={onClick}
    />
  );
};

export const AiAgentsGoalsBadge = ({
  goals,
  onClick,
}: {
  goals: AiAgentSessionGoal[];
  onClick: () => void;
}) => {
  const { t } = useTranslation(['conversations']);
  if (!goals.length) return null;

  const allGoalsAchieved = goals.every((goal) => goal.status === 'ACHIEVED');

  return (
    <InteractionDetailsBadge
      icon="circularCheck"
      data-lgg-id="ai-agents-goals-badge"
      title={t(
        allGoalsAchieved
          ? 'conversations:contactInteractionBubble.aiAgentSessionDetails.badges.goalAchieved'
          : 'conversations:contactInteractionBubble.aiAgentSessionDetails.badges.goalPartiallyAchieved',
        { count: goals.length },
      )}
      type={allGoalsAchieved ? 'success' : 'partially-achieved'}
      onClick={onClick}
    />
  );
};

export const AiAgentsActionsBadge = ({
  actionsCount,
  onClick,
}: {
  actionsCount: number;
  onClick: () => void;
}) => {
  const { t } = useTranslation(['conversations']);

  if (!actionsCount) return null;

  return (
    <InteractionDetailsBadge
      icon="aiAgentAction"
      data-lgg-id="ai-agents-actions-badge"
      title={t(
        'conversations:contactInteractionBubble.aiAgentSessionDetails.badges.actionsPerformed',
        { count: actionsCount },
      )}
      type="neutral"
      onClick={onClick}
    />
  );
};

export const TakenOverByBadge = ({
  agentName,
  onClick,
}: {
  agentName: string | null;
  onClick: () => void;
}) => {
  const { t } = useTranslation(['conversations']);

  if (!agentName) return null;

  return (
    <InteractionDetailsBadge
      icon="assignedto"
      data-lgg-id="taken-over-by-badge"
      title={t(
        'conversations:contactInteractionBubble.aiAgentSessionDetails.badges.takenOverBy',
        { agentName },
      )}
      type="neutral"
      onClick={onClick}
    />
  );
};

export const AiAgentsInvolvedBadge = ({
  agentsCount,
  onClick,
}: {
  agentsCount: number;
  onClick: () => void;
}) => {
  const { t } = useTranslation(['conversations']);

  if (!agentsCount || agentsCount === 1) return null;

  return (
    <InteractionDetailsBadge
      icon="ivrSpeaker"
      data-lgg-id="ai-agents-involved-badge"
      title={t(
        'conversations:contactInteractionBubble.aiAgentSessionDetails.badges.aiAgentsInvolved',
        { count: agentsCount },
      )}
      type="neutral"
      onClick={onClick}
    />
  );
};

export const SectionTitle = styled.p<{ $compact?: boolean }>`
  -webkit-text-stroke: 1px rgba(0, 0, 0, 0);
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  letter-spacing: -0.26px;
  line-height: ${({ $compact }) => ($compact ? '16px' : '15px')};
  margin: 0 10px ${({ $compact }) => ($compact ? '4px' : '10px')};
  text-align: left;
  text-wrap: nowrap;
`;

export const SectionContainer = styled(FlexColumn)<{ $compact?: boolean }>`
  padding: 15px 10px ${({ $compact }) => ($compact ? '15px' : '20px')};
`;

export const SectionDivider = styled.div`
  background-color: ${({ theme }) => theme.colors.koala};
  height: 1px;
  margin: 0;
`;

const AiAgentNameContainer = styled(FlexRow)`
  gap: 8px;
  margin-bottom: 4px;
`;

const AiAgentName = styled.span`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 16px;
`;
const AiAgentIcon = styled(Icon)`
  svg {
    width: 16px;
    path {
      fill: ${({ theme }) => theme.colors.secondaryTopaz};
    }
  }
`;

const AiAgentListContainer = styled(FlexColumn)`
  gap: 15px;
  margin: 0 10px;
`;

const AiAgentActionsList = styled.ul<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '8px' : '2px')};
  list-style: none;
  margin-left: 22px;
  margin: 0;
  padding: 0;
  width: 100%;

  li {
    align-items: center;
    color: ${({ theme }) => theme.colors.flint};
    display: flex;
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 12px;
    gap: 6px;
    line-height: 20px;

    :before {
      align-items: center;
      background-color: ${({ theme }) => theme.colors.porcelain};
      border-radius: 3px;
      color: ${({ theme }) => theme.colors.gogo};
      content: '•';
      display: flex;
      font-size: 20px;
      height: 16px;
      justify-content: center;
      padding-bottom: 2px;
      width: 16px;
    }
  }
`;

const AiAgentListItemContent = styled(FlexRow)`
  justify-content: space-between;
  width: 100%;
`;

const AiAgentListItemContainer = styled.span<{ $compact?: boolean }>`
  align-items: center;
  display: flex;
  ${({ $compact }) => $compact && 'font-size: 13px;'}
`;

const AiAgentListsContainer = styled(FlexColumn)`
  gap: 15px;
`;

const GoalStatusBadge = styled(BaseBadge)<{
  $type?: 'achieved' | 'not-achieved';
}>`
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
  height: 16px;
  letter-spacing: -0.22px;
  line-height: 15px;
  line-height: 15px;
  margin: 0;
  padding: 1px 4px;

  ${({ $type }) =>
    $type === 'achieved'
      ? css`
          background-color: ${({ theme }) => theme.colors.secondaryMint10};
          border: 1px solid ${({ theme }) => theme.colors.secondaryMint30};

          .badge-content {
            color: ${({ theme }) => theme.colors.secondaryMintDark};
          }
        `
      : css`
          background-color: ${({ theme }) => theme.colors.porcelain};
          border: 1px solid ${({ theme }) => theme.colors.koala};

          .badge-content {
            color: ${({ theme }) => theme.colors.flint};
          }
        `}
`;

export const InteractionTruncatedTextDetail = styled(InteractionTextContent)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  font-family: ${({ theme }) => theme.font.regular};
  letter-spacing: unset;
  margin: 0 0 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: break-spaces;

  ${up('md')} {
    -webkit-line-clamp: 2;
    font-size: 13px;
    letter-spacing: unset;
    line-height: 15px;
  }
`;

type AiAgentTitleProps = {
  name: string;
  goals?: {
    total: number;
    achieved: number;
  };
};

const AiAgentTitle = (props: AiAgentTitleProps) => {
  const { name, goals } = props;

  return (
    <AiAgentNameContainer data-lgg-id="ai-agent-title">
      <AiAgentIcon type="ivrSpeaker" />
      <AiAgentName>{`${name} ${
        goals ? `(${goals.achieved}/${goals.total})` : ''
      }`}</AiAgentName>
    </AiAgentNameContainer>
  );
};

type AiAgentUnorderedListProps = {
  items: {
    id: string;
    decription: string;
    extraContent?: React.ReactNode;
  }[];
  compact?: boolean;
};

const AiAgentUnorderedList = (props: AiAgentUnorderedListProps) => {
  const { items, compact } = props;

  return (
    <FlexRow>
      <AiAgentActionsList $compact={compact}>
        {items.map((item) => (
          <li key={item.id}>
            <AiAgentListItemContent data-lgg-id={`ai-agent-list-item-content-${item.id}`}>
              <AiAgentListItemContainer $compact={compact}>
                {item.decription}
              </AiAgentListItemContainer>
              <AiAgentListItemContainer>{item.extraContent}</AiAgentListItemContainer>
            </AiAgentListItemContent>
          </li>
        ))}
      </AiAgentActionsList>
    </FlexRow>
  );
};

const CallSummary = styled.p`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  letter-spacing: normal;
  line-height: 1.54;
  margin: 0 10px;
  text-align: left;
`;

type SummaryDetailSectionProps = {
  title: string;
  content: string;
  testId?: string;
  compact?: boolean;
};

export const SummaryDetailSection = ({
  title,
  content,
  testId,
  compact,
}: SummaryDetailSectionProps) => {
  return (
    <SectionContainer data-lgg-id={testId} $compact={compact}>
      <SectionTitle $compact={compact}>{title}</SectionTitle>
      <CallSummary data-lgg-id="summary-value">{content}</CallSummary>
    </SectionContainer>
  );
};

export type SingleSessionView = {
  summaryTitle: string;
  sentimentsTitle: string;
  goalsTitle: (achieved: number, total: number) => string;
  actionsTitle: string;
};

type GoalsDetailSectionProps = {
  records: {
    agent: AiAgent;
    goals: AiAgentSessionGoal[];
  }[];
  singleSessionView?: SingleSessionView;
};

const countAchievedGoals = (goals: AiAgentSessionGoal[]) =>
  goals.filter((goal) => goal.status === 'ACHIEVED').length;

export const GoalsDetailSection = ({
  records,
  singleSessionView,
}: GoalsDetailSectionProps) => {
  const { t } = useTranslation(['conversations']);
  const allGoals = records.flatMap(({ goals }) => goals);
  const compact = !!singleSessionView;

  return (
    <span>
      <SectionDivider />
      <SectionContainer data-lgg-id="ai-agents-goals-section" $compact={compact}>
        <SectionTitle $compact={compact}>
          {singleSessionView
            ? singleSessionView.goalsTitle(countAchievedGoals(allGoals), allGoals.length)
            : t(
                'conversations:contactInteractionBubble.aiAgentSessionDetails.aiAgentsGoals.title',
              )}
        </SectionTitle>
        <AiAgentListsContainer>
          {records.map(({ agent, goals }) => {
            const totalGoals = goals.length;
            const achievedGoals = countAchievedGoals(goals);
            const key = `goals-${agent.id}`;

            return (
              <AiAgentListContainer key={key} data-lgg-id={key}>
                <span>
                  {compact ? null : (
                    <AiAgentTitle
                      name={agent.name}
                      goals={{ total: totalGoals, achieved: achievedGoals }}
                    />
                  )}
                  <AiAgentUnorderedList
                    compact={compact}
                    items={goals.map((goal) => ({
                      id: goal.id,
                      decription: goal.name,
                      extraContent:
                        goal.status === 'ACHIEVED' ? (
                          <GoalStatusBadge
                            badgeContent={t(
                              'conversations:contactInteractionBubble.aiAgentSessionDetails.aiAgentsGoals.goalStatus.achieved',
                            )}
                            $type="achieved"
                          />
                        ) : (
                          <GoalStatusBadge
                            badgeContent={t(
                              'conversations:contactInteractionBubble.aiAgentSessionDetails.aiAgentsGoals.goalStatus.notAchieved',
                            )}
                            $type="not-achieved"
                          />
                        ),
                    }))}
                  />
                </span>
              </AiAgentListContainer>
            );
          })}
        </AiAgentListsContainer>
      </SectionContainer>
    </span>
  );
};

type ActionsDetailSectionProps = {
  records: {
    agent: AiAgent;
    actions: AiAgentToolCall[];
  }[];
  singleSessionView?: SingleSessionView;
};

export const ActionsDetailSection = ({
  records,
  singleSessionView,
}: ActionsDetailSectionProps) => {
  const { t } = useTranslation(['conversations']);
  const compact = !!singleSessionView;

  return (
    <span>
      <SectionDivider />
      <SectionContainer data-lgg-id="ai-agents-actions-section" $compact={compact}>
        <SectionTitle $compact={compact}>
          {singleSessionView?.actionsTitle ??
            t(
              'conversations:contactInteractionBubble.aiAgentSessionDetails.aiAgentsActions.title',
            )}
        </SectionTitle>
        <AiAgentListsContainer>
          {records.map(({ agent, actions }) => {
            return (
              <AiAgentListContainer key={`actions-performed-by-${agent.id}`}>
                <span>
                  {compact ? null : <AiAgentTitle name={agent.name} />}
                  <AiAgentUnorderedList
                    compact={compact}
                    items={actions.map((action) => ({
                      id: action.id,
                      decription: action.name,
                    }))}
                  />
                </span>
              </AiAgentListContainer>
            );
          })}
        </AiAgentListsContainer>
      </SectionContainer>
    </span>
  );
};

const TakenOverByAssigneeLabel = styled(AssigneeLabel)<{ $compact?: boolean }>`
  overflow: hidden;

  .assignee-label-avatar {
    height: 20px;
    width: 20px;
  }

  .assignee-label-name {
    color: ${({ theme }) => theme.colors.smalt};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 13px;
    margin-left: 5px;
    line-height: ${({ $compact }) => ($compact ? '16px' : '17px')};
  }
`;

const StyledSectionTitle = styled(SectionTitle)`
  margin-bottom: 0;
`;

const ContentContainer = styled(FlexRow)`
  align-items: center;
`;

export const TakenOverByDetailSection = ({
  takenOverBy,
  compact,
}: {
  takenOverBy?: User;
  compact?: boolean;
}) => {
  const { t } = useTranslation(['conversations']);

  if (!takenOverBy) return null;

  return (
    <>
      <SectionDivider />
      <SectionContainer data-lgg-id="taken-over-by-section" $compact={compact}>
        <ContentContainer>
          <StyledSectionTitle $compact={compact}>
            {t(
              'conversations:contactInteractionBubble.aiAgentSessionDetails.takenOverBy.title',
            )}
          </StyledSectionTitle>
          <TakenOverByAssigneeLabel
            assignee={takenOverBy}
            data-lgg-id="taken-over-by-label"
            $compact={compact}
          />
        </ContentContainer>
      </SectionContainer>
    </>
  );
};

const BRIEF_MAX_LENGTH = 200;

export const getInteractionDetailBrief = (summary?: string) => {
  if (!summary) return null;

  const summaryLength = summary.length;
  const briefText = summary.slice(0, BRIEF_MAX_LENGTH);

  return `${briefText} ${summaryLength > BRIEF_MAX_LENGTH ? '...' : ''}`;
};

export const MoreDetailsOption = styled.div`
  color: ${({ theme }) => theme.colors.gogo};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 14px;
  margin-top: 5px;
`;
